import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Edit2, Check, X, RefreshCw, IndianRupee,
  Shirt, Sparkles, Footprints, Home, Repeat, Plus, Trash2, Wrench, Lock
} from 'lucide-react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { id: 'laundry',  label: 'Wash & Iron',      icon: Shirt },
  { id: 'dry',      label: 'Dry Cleaning',      icon: Sparkles },
  { id: 'shoe',     label: 'Shoe Cleaning',     icon: Footprints },
  { id: 'home',     label: 'Home Items',        icon: Home },
  { id: 'foldpass', label: 'FoldPass Plans',    icon: Repeat },
  { id: 'addon',    label: 'Add-ons',           icon: Plus },
  { id: 'services', label: 'Manage Services',   icon: Wrench },
];

const LAUNDRY_COLORS = {
  flexfold:      { bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-200' },
  flashfold:     { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  justpress:     { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200' },
  laundry_extra: { bg: 'bg-teal-50',   text: 'text-teal-700',   border: 'border-teal-200' },
};

// ── Inline price editor ───────────────────────────────────────────────────────
const PriceCell = ({ rate, onSave, token }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  const [saving, setSaving] = useState(false);

  const startEdit = () => { setVal(String(rate.price)); setEditing(true); };
  const cancel = () => setEditing(false);

  const save = async () => {
    const parsed = parseFloat(val);
    if (isNaN(parsed) || parsed < 0) { toast.error('Enter a valid price'); return; }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/individual-rates/${rate.id}`,
        { price: parsed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSave(res.data);
      setEditing(false);
      toast.success(`${rate.label} updated to ₹${parsed}`);
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1.5">
        <span className="text-slate-400 text-sm">₹</span>
        <input autoFocus type="number" value={val}
          onChange={e => setVal(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancel(); }}
          className="w-24 px-2 py-1.5 border border-blue-400 rounded-lg text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-200"
        />
        <button onClick={save} disabled={saving}
          className="w-7 h-7 rounded-lg bg-emerald-600 hover:bg-emerald-700 flex items-center justify-center text-white disabled:opacity-50 transition-colors">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
        </button>
        <button onClick={cancel} className="w-7 h-7 rounded-lg bg-slate-200 hover:bg-slate-300 flex items-center justify-center text-slate-600 transition-colors">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span className="font-bold text-slate-800">₹{rate.price}</span>
      <button onClick={startEdit}
        className="w-7 h-7 rounded-lg bg-blue-50 hover:bg-blue-100 flex items-center justify-center text-blue-600 transition-colors">
        <Edit2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

// ── Add Item Modal ────────────────────────────────────────────────────────────
const CAT_LABELS = {
  dry_cleaning:  'Dry Cleaning',
  shoe_cleaning: 'Shoe Cleaning',
  home_items:    'Home Item',
  laundry_extra: 'Wash & Iron Service',
  foldpass_plan: 'FoldPass Plan',
  foldpass_addon:'FoldPass Add-on',
};

const AddItemModal = ({ category, defaultGroup, onClose, onCreated, token }) => {
  const [form, setForm] = useState({
    label: '', price: '', unit: 'piece', item_group: defaultGroup || '',
    plan_kg: '', plan_months: '1', plan_popular: false,
    addon_kg: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    const price = parseFloat(form.price);
    if (isNaN(price) || price < 0) { toast.error('Enter a valid price'); return; }

    if (category === 'foldpass_plan') {
      if (!form.plan_kg || parseInt(form.plan_kg) <= 0) { toast.error('KG amount is required'); return; }
      if (!form.plan_months || parseInt(form.plan_months) <= 0) { toast.error('Validity months is required'); return; }
      if (!form.label.trim()) { toast.error('Plan name is required'); return; }
    } else if (category === 'foldpass_addon') {
      if (!form.addon_kg || parseInt(form.addon_kg) <= 0) { toast.error('Add-on KG is required'); return; }
      if (!form.label.trim()) { toast.error('Add-on name is required'); return; }
    } else {
      if (!form.label.trim()) { toast.error('Item name is required'); return; }
      if (category === 'dry_cleaning' && !form.item_group) { toast.error('Select a group'); return; }
    }

    setSaving(true);
    try {
      const payload = {
        category,
        label: form.label.trim(),
        catalog_item: form.label.trim(),
        price,
        unit: form.unit,
        item_group: category === 'dry_cleaning' ? form.item_group : null,
        ...(category === 'foldpass_plan' ? {
          plan_kg: parseInt(form.plan_kg),
          plan_months: parseInt(form.plan_months),
          plan_popular: form.plan_popular,
        } : {}),
        ...(category === 'foldpass_addon' ? { addon_kg: parseInt(form.addon_kg) } : {}),
      };
      const res = await axios.post(`${API}/admin/individual-rates`, payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`"${form.label}" added`);
      onCreated(res.data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create item');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-black text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Add {CAT_LABELS[category] || category}
          </h2>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Group picker — dry cleaning only */}
        {category === 'dry_cleaning' && (
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Group</label>
            <div className="flex gap-2">
              {['Women', 'Men', 'Kids'].map(g => (
                <button key={g} onClick={() => set('item_group', g)}
                  className={`flex-1 py-2 rounded-xl text-sm font-semibold transition-colors ${form.item_group === g ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                  {g}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* FoldPass Plan fields */}
        {category === 'foldpass_plan' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">KG Amount</label>
              <input type="number" placeholder="e.g. 5" value={form.plan_kg}
                onChange={e => set('plan_kg', e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Valid (Months)</label>
              <input type="number" placeholder="e.g. 1" value={form.plan_months}
                onChange={e => set('plan_months', e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
            <div className="col-span-2 flex items-center gap-3">
              <button onClick={() => set('plan_popular', !form.plan_popular)}
                className={`w-10 h-6 rounded-full transition-colors ${form.plan_popular ? 'bg-blue-600' : 'bg-slate-200'} relative`}>
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${form.plan_popular ? 'left-5' : 'left-1'}`} />
              </button>
              <span className="text-sm text-slate-600">Mark as Popular plan</span>
            </div>
          </div>
        )}

        {/* FoldPass Add-on fields */}
        {category === 'foldpass_addon' && (
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Add-on KG Amount</label>
            <input type="number" placeholder="e.g. 3" value={form.addon_kg}
              onChange={e => set('addon_kg', e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
          </div>
        )}

        {/* Label */}
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
            {category === 'foldpass_plan' ? 'Plan Name' : category === 'foldpass_addon' ? 'Add-on Name' : 'Item Name'}
          </label>
          <input autoFocus type="text"
            placeholder={
              category === 'foldpass_plan' ? 'e.g. FoldPass 5kg' :
              category === 'foldpass_addon' ? 'e.g. +3kg Pack' :
              category === 'laundry_extra' ? 'e.g. Woolen Wash, Premium Wash' :
              category === 'shoe_cleaning' ? 'e.g. Boots, Heels' :
              category === 'home_items' ? 'e.g. Sofa Cover' : 'e.g. Kurta / Kameez'
            }
            value={form.label}
            onChange={e => set('label', e.target.value)}
            onKeyDown={e => e.key === 'Enter' && submit()}
            className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
          />
        </div>

        {/* Price + Unit */}
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Price (₹)</label>
            <input type="number" placeholder="0" value={form.price}
              onChange={e => set('price', e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
          </div>
          {!['foldpass_plan', 'foldpass_addon', 'dry_cleaning', 'shoe_cleaning'].includes(category) && (
            <div className="flex-1">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Unit</label>
              <select value={form.unit} onChange={e => set('unit', e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 bg-white">
                {(category === 'home_items' ? ['piece', 'panel', 'sqft'] : ['piece', 'kg']).map(u => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Price hint */}
        {category === 'laundry_extra' && (
          <p className="text-xs text-slate-400 -mt-2">This service will appear on the customer Pricing page under Wash & Iron.</p>
        )}
        {category === 'foldpass_plan' && (
          <p className="text-xs text-slate-400 -mt-2">New plan will appear in the FoldPass subscription page. Plan type key: {form.plan_kg ? `${form.plan_kg}kg` : '—'}</p>
        )}

        <div className="flex gap-3 pt-1">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 transition-colors">
            Cancel
          </button>
          <button onClick={submit} disabled={saving}
            className="flex-1 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Generic catalog table with optional delete ────────────────────────────────
const CatalogTable = ({ items, onSave, onDelete, deletingId, token }) => (
  <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-slate-50 border-b border-slate-200">
          <th className="px-4 py-3 text-left text-xs font-black text-slate-500 uppercase tracking-wider">Item</th>
          <th className="px-4 py-3 text-left text-xs font-black text-slate-500 uppercase tracking-wider">Unit</th>
          <th className="px-4 py-3 text-left text-xs font-black text-slate-500 uppercase tracking-wider">Price</th>
          {onDelete && <th className="px-4 py-3 w-10" />}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {items.length === 0 && (
          <tr><td colSpan={onDelete ? 4 : 3} className="px-4 py-8 text-center text-slate-400 text-sm">No items yet — click Add Item to create one</td></tr>
        )}
        {items.map(rate => (
          <tr key={rate.id} className="hover:bg-slate-50/60 transition-colors">
            <td className="px-4 py-3 font-medium text-slate-700">{rate.label}</td>
            <td className="px-4 py-3 text-slate-400 capitalize">{rate.unit}</td>
            <td className="px-4 py-3"><PriceCell rate={rate} onSave={onSave} token={token} /></td>
            {onDelete && (
              <td className="px-4 py-3">
                <button onClick={() => onDelete(rate)} disabled={deletingId === rate.id}
                  className="w-7 h-7 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center text-red-500 transition-colors disabled:opacity-40">
                  {deletingId === rate.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                </button>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ── Tab with Add button header ─────────────────────────────────────────────────
const TabHeader = ({ label, onAdd }) => (
  <div className="flex items-center justify-between mb-4">
    <p className="text-xs text-slate-400">{label}</p>
    <button onClick={onAdd}
      className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-xl transition-colors">
      <Plus className="w-4 h-4" /> Add Item
    </button>
  </div>
);

// ── Service Category Settings (Manage Services tab) ─────────────────────────────
const DaysEditor = ({ cat, onSave, token }) => {
  const [editing, setEditing] = useState(false);
  const [defaultDays, setDefaultDays] = useState(String(cat.default_days));
  const [maxDays, setMaxDays] = useState(String(cat.max_days));
  const [saving, setSaving] = useState(false);

  const startEdit = () => { setDefaultDays(String(cat.default_days)); setMaxDays(String(cat.max_days)); setEditing(true); };

  const save = async () => {
    const d = parseInt(defaultDays), m = parseInt(maxDays);
    if (isNaN(d) || isNaN(m) || d < 1 || m < d) { toast.error('Max days must be >= default days, both >= 1'); return; }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/service-categories/${cat.id}`, { default_days: d, max_days: m },
        { headers: { Authorization: `Bearer ${token}` } });
      onSave(res.data);
      setEditing(false);
      toast.success(`${cat.label} delivery days updated`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1.5">
        <input type="number" min="1" value={defaultDays} onChange={e => setDefaultDays(e.target.value)}
          className="w-14 px-2 py-1 border border-blue-400 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-200" />
        <span className="text-slate-300 text-xs">/</span>
        <input type="number" min="1" value={maxDays} onChange={e => setMaxDays(e.target.value)}
          className="w-14 px-2 py-1 border border-blue-400 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-200" />
        <button onClick={save} disabled={saving} className="w-6 h-6 rounded-lg bg-emerald-600 hover:bg-emerald-700 flex items-center justify-center text-white disabled:opacity-50">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
        </button>
        <button onClick={() => setEditing(false)} className="w-6 h-6 rounded-lg bg-slate-200 hover:bg-slate-300 flex items-center justify-center text-slate-600">
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }

  return (
    <button onClick={startEdit} className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-blue-600 transition-colors">
      {cat.default_days}d <span className="text-slate-300 text-xs">(max {cat.max_days}d)</span>
      <Edit2 className="w-3 h-3 text-slate-300" />
    </button>
  );
};

const ToggleSwitch = ({ on, onToggle }) => (
  <button onClick={onToggle} className={`w-10 h-6 rounded-full transition-colors relative ${on ? 'bg-blue-600' : 'bg-slate-200'}`}>
    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${on ? 'left-5' : 'left-1'}`} />
  </button>
);

const AddServiceCategoryModal = ({ onClose, onCreated, token }) => {
  const [form, setForm] = useState({ label: '', price: '', gst_applicable: false, is_fast: false, default_days: '3', max_days: '5' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.label.trim()) { toast.error('Service name is required'); return; }
    const price = parseFloat(form.price);
    if (isNaN(price) || price < 0) { toast.error('Enter a valid price per kg'); return; }
    const d = parseInt(form.default_days), m = parseInt(form.max_days);
    if (isNaN(d) || isNaN(m) || d < 1 || m < d) { toast.error('Max days must be >= default days, both >= 1'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/service-categories`, {
        label: form.label.trim(), price, gst_applicable: form.gst_applicable,
        is_fast: form.is_fast, default_days: d, max_days: m,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`"${form.label}" added — now live on the pricing page and delivery checkout`);
      onCreated(res.data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create service');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6 space-y-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-black text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>New Service</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-slate-400 -mt-3">Priced per kg, just like FlexFold/JustPress. Appears on the pricing page and delivery checkout instantly.</p>

        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Service Name</label>
          <input autoFocus type="text" placeholder="e.g. Wash & Fold" value={form.label}
            onChange={e => set('label', e.target.value)}
            onKeyDown={e => e.key === 'Enter' && submit()}
            className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400" />
        </div>

        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Price per KG (₹)</label>
          <input type="number" placeholder="e.g. 69" value={form.price}
            onChange={e => set('price', e.target.value)}
            className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Default Days</label>
            <input type="number" min="1" value={form.default_days} onChange={e => set('default_days', e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Max Days</label>
            <input type="number" min="1" value={form.max_days} onChange={e => set('max_days', e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
          </div>
        </div>

        <div className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.gst_applicable} onChange={e => set('gst_applicable', e.target.checked)} className="w-4 h-4 rounded" />
            <span className="text-sm text-slate-600">GST applicable (18%)</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.is_fast} onChange={e => set('is_fast', e.target.checked)} className="w-4 h-4 rounded" />
            <span className="text-sm text-slate-600">Fast turnaround (splits the order like JustPress/FlashFold when combined with slower services)</span>
          </label>
        </div>

        <div className="flex gap-3 pt-1">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 transition-colors">
            Cancel
          </button>
          <button onClick={submit} disabled={saving}
            className="flex-1 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Create Service
          </button>
        </div>
      </div>
    </div>
  );
};

const ServiceCategoriesTab = ({ token, rates, onRateSave }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/service-categories`, { headers: { Authorization: `Bearer ${token}` } });
      setCategories(res.data || []);
    } catch {
      toast.error('Failed to load services');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const handleSave = (updated) => setCategories(prev => prev.map(c => c.id === updated.id ? updated : c));
  const handleCreated = (created) => setCategories(prev => [...prev, created]);

  const handleToggle = async (cat, field) => {
    try {
      const res = await axios.put(`${API}/admin/service-categories/${cat.id}`, { [field]: !cat[field] },
        { headers: { Authorization: `Bearer ${token}` } });
      handleSave(res.data);
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`Delete "${cat.label}"? This cannot be undone — existing orders keep their data, but the service disappears from checkout.`)) return;
    setDeletingId(cat.id);
    try {
      await axios.delete(`${API}/admin/service-categories/${cat.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setCategories(prev => prev.filter(c => c.id !== cat.id));
      toast.success(`"${cat.label}" deleted`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    } finally { setDeletingId(null); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-24">
      <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
    </div>
  );

  return (
    <div className="space-y-4">
      <TabHeader label="Delivery days & GST for every service (including built-in ones like JustPress), plus custom kg-priced services" onAdd={() => setShowAdd(true)} />
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-4 py-3 text-left text-xs font-black text-slate-500 uppercase tracking-wider">Service</th>
                <th className="px-4 py-3 text-left text-xs font-black text-slate-500 uppercase tracking-wider">Delivery Days</th>
                <th className="px-4 py-3 text-left text-xs font-black text-slate-500 uppercase tracking-wider">Price</th>
                <th className="px-4 py-3 text-left text-xs font-black text-slate-500 uppercase tracking-wider">GST</th>
                <th className="px-4 py-3 text-left text-xs font-black text-slate-500 uppercase tracking-wider">Fast</th>
                <th className="px-4 py-3 text-left text-xs font-black text-slate-500 uppercase tracking-wider">Active</th>
                <th className="px-4 py-3 w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {categories.map(cat => {
                const rate = rates.find(r => r.category === cat.key && r.unit === 'kg');
                return (
                  <tr key={cat.id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-700">{cat.label}</span>
                        {cat.is_system ? (
                          <span className="flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-400"><Lock className="w-2.5 h-2.5" />Built-in</span>
                        ) : (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-600">Custom</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3"><DaysEditor cat={cat} onSave={handleSave} token={token} /></td>
                    <td className="px-4 py-3">
                      {rate ? <PriceCell rate={rate} onSave={onRateSave} token={token} /> : <span className="text-slate-300 text-xs">catalog pricing</span>}
                    </td>
                    <td className="px-4 py-3"><ToggleSwitch on={cat.gst_applicable} onToggle={() => handleToggle(cat, 'gst_applicable')} /></td>
                    <td className="px-4 py-3"><ToggleSwitch on={cat.is_fast} onToggle={() => handleToggle(cat, 'is_fast')} /></td>
                    <td className="px-4 py-3"><ToggleSwitch on={cat.is_active} onToggle={() => handleToggle(cat, 'is_active')} /></td>
                    <td className="px-4 py-3">
                      {!cat.is_system && (
                        <button onClick={() => handleDelete(cat)} disabled={deletingId === cat.id}
                          className="w-7 h-7 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center text-red-500 transition-colors disabled:opacity-40">
                          {deletingId === cat.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {showAdd && <AddServiceCategoryModal token={token} onClose={() => setShowAdd(false)} onCreated={handleCreated} />}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const AdminPriceDashboard = () => {
  const navigate = useNavigate();
  const { token, isSuperAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('laundry');
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addModal, setAddModal] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchRates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/individual-rates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRates(res.data || []);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message || 'unknown';
      toast.error(`Failed to load rates (${status || 'network'}: ${detail})`);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token || !isSuperAdmin()) { navigate('/dashboard'); return; }
    fetchRates();
  }, [fetchRates]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = (updated) => setRates(prev => prev.map(r => r.id === updated.id ? updated : r));
  const handleCreated = (newRate) => setRates(prev => [...prev, newRate]);

  const handleDelete = async (rate) => {
    if (!window.confirm(`Delete "${rate.label}"? This cannot be undone.`)) return;
    setDeletingId(rate.id);
    try {
      await axios.delete(`${API}/admin/individual-rates/${rate.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRates(prev => prev.filter(r => r.id !== rate.id));
      toast.success(`"${rate.label}" deleted`);
    } catch {
      toast.error('Failed to delete item');
    } finally {
      setDeletingId(null);
    }
  };

  const byCategory = (cat) => rates.filter(r => r.category === cat);
  const byGroup = (cat, group) => rates.filter(r => r.category === cat && r.item_group === group);

  // ── Tabs ──────────────────────────────────────────────────────────────────────
  const LaundryTab = () => {
    const coreItems = ['flexfold', 'flashfold', 'justpress'].map(cat => byCategory(cat)[0]).filter(Boolean);
    const extras = byCategory('laundry_extra');
    return (
      <div className="space-y-6">
        {/* Fixed services */}
        <div>
          <p className="text-xs text-slate-400 mb-3">Core services — edit price only</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {coreItems.map(rate => {
              const c = LAUNDRY_COLORS[rate.category] || LAUNDRY_COLORS.flexfold;
              return (
                <div key={rate.id} className={`bg-white border ${c.border} rounded-2xl p-5 shadow-sm`}>
                  <div className={`w-10 h-10 rounded-xl ${c.bg} flex items-center justify-center mb-4`}>
                    <Shirt className={`w-5 h-5 ${c.text}`} />
                  </div>
                  <p className="font-black text-slate-800 text-base mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{rate.label}</p>
                  <p className="text-xs text-slate-400 mb-4">Per {rate.unit}</p>
                  <PriceCell rate={rate} onSave={handleSave} token={token} />
                  <p className="text-[10px] text-slate-300 mt-2">
                    Updated: {new Date(rate.updated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
        {/* Custom services */}
        <div>
          <TabHeader label="Custom wash & iron services (appear on pricing page)" onAdd={() => setAddModal({ category: 'laundry_extra', defaultGroup: null })} />
          <CatalogTable items={extras} onSave={handleSave} onDelete={handleDelete} deletingId={deletingId} token={token} />
        </div>
      </div>
    );
  };

  const DryCleanTab = () => {
    const [dcGroup, setDcGroup] = useState('Women');
    return (
      <div>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex gap-2 flex-wrap">
            {['Women', 'Men', 'Kids'].map(g => (
              <button key={g} onClick={() => setDcGroup(g)}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${dcGroup === g ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{g}
              </button>
            ))}
          </div>
          <button onClick={() => setAddModal({ category: 'dry_cleaning', defaultGroup: dcGroup })}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-xl transition-colors">
            <Plus className="w-4 h-4" /> Add Item
          </button>
        </div>
        <CatalogTable items={byGroup('dry_cleaning', dcGroup)} onSave={handleSave} onDelete={handleDelete} deletingId={deletingId} token={token} />
      </div>
    );
  };

  const FoldPassTab = () => {
    const plans = byCategory('foldpass_plan').sort((a, b) => (a.plan_kg || 0) - (b.plan_kg || 0));
    return (
      <div className="space-y-4">
        <TabHeader label="FoldPass subscription plans — new plans appear in the customer subscription page" onAdd={() => setAddModal({ category: 'foldpass_plan', defaultGroup: null })} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans.map(rate => (
            <div key={rate.id} className={`bg-white border rounded-2xl p-5 shadow-sm relative ${rate.plan_popular ? 'border-blue-400 ring-2 ring-blue-100' : 'border-slate-200'}`}>
              {rate.plan_popular && (
                <span className="inline-block text-[10px] font-black bg-blue-600 text-white px-2.5 py-0.5 rounded-full mb-3 uppercase tracking-wider">Popular</span>
              )}
              <button onClick={() => handleDelete(rate)} disabled={deletingId === rate.id}
                className="absolute top-3 right-3 w-7 h-7 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center text-red-500 transition-colors disabled:opacity-40">
                {deletingId === rate.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              </button>
              <p className="font-black text-slate-800 text-base mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{rate.label}</p>
              <p className="text-xs text-slate-400 mb-4">{rate.plan_kg}kg · {rate.plan_months} month{rate.plan_months > 1 ? 's' : ''}</p>
              <PriceCell rate={rate} onSave={handleSave} token={token} />
              <p className="text-[10px] text-slate-300 mt-2">
                Updated: {new Date(rate.updated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
            </div>
          ))}
          {plans.length === 0 && <p className="text-sm text-slate-400 col-span-3 py-6 text-center">No plans — click Add Item to create one</p>}
        </div>
      </div>
    );
  };

  const AddonsTab = () => {
    const addons = byCategory('foldpass_addon').sort((a, b) => (a.addon_kg || 0) - (b.addon_kg || 0));
    return (
      <div className="space-y-4">
        <TabHeader label="FoldPass KG add-on packs" onAdd={() => setAddModal({ category: 'foldpass_addon', defaultGroup: null })} />
        <CatalogTable items={addons} onSave={handleSave} onDelete={handleDelete} deletingId={deletingId} token={token} />
      </div>
    );
  };

  const renderTab = () => {
    if (loading) return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
    const tableTab = (cat) => (
      <div className="space-y-4">
        <TabHeader label={`${CAT_LABELS[cat]} items`} onAdd={() => setAddModal({ category: cat, defaultGroup: null })} />
        <CatalogTable items={byCategory(cat)} onSave={handleSave} onDelete={handleDelete} deletingId={deletingId} token={token} />
      </div>
    );
    switch (activeTab) {
      case 'laundry':  return <LaundryTab />;
      case 'dry':      return <DryCleanTab />;
      case 'shoe':     return tableTab('shoe_cleaning');
      case 'home':     return tableTab('home_items');
      case 'foldpass': return <FoldPassTab />;
      case 'addon':    return <AddonsTab />;
      case 'services': return <ServiceCategoriesTab token={token} rates={rates} onRateSave={handleSave} />;
      default:         return null;
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Price Dashboard</h1>
          <p className="text-slate-400 text-sm mt-0.5">Edit prices, add new items, or remove old ones — changes apply instantly</p>
        </div>
        <button onClick={fetchRates}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 text-sm font-semibold transition-colors">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="bg-blue-50 border border-blue-100 rounded-2xl px-5 py-3 flex items-center gap-3">
        <IndianRupee className="w-4 h-4 text-blue-600 flex-shrink-0" />
        <p className="text-sm text-blue-700">
          <strong>Pencil</strong> = edit price · <strong>Green Add Item</strong> = create new entry (appears on pricing page instantly) · <strong>Red trash</strong> = delete
        </p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
              activeTab === id ? 'bg-blue-600 text-white shadow-md shadow-blue-200' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}>
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
      </div>

      <div>{renderTab()}</div>

      {addModal && (
        <AddItemModal
          category={addModal.category}
          defaultGroup={addModal.defaultGroup}
          token={token}
          onCreated={handleCreated}
          onClose={() => setAddModal(null)}
        />
      )}
    </div>
  );
};

export default AdminPriceDashboard;
